import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import {
  addProjectArtstyleReference as addProjectArtstyleReferenceClient,
  archiveProject as archiveProjectClient,
  createProject as createProjectClient,
  deleteProject as deleteProjectClient,
  formatProjectErrorMessage,
  getCurrentProjectCapacity,
  getProjectAccessSelectionState,
  getProjectDashboardSummary,
  getProjectImageSettings as getProjectImageSettingsClient,
  listProjects,
  ProjectActionError,
  removeProjectArtstyleReference as removeProjectArtstyleReferenceClient,
  reorderProjectArtstyleReferences as reorderProjectArtstyleReferencesClient,
  submitProjectAccessSelection as submitProjectAccessSelectionClient,
  replaceProjectArtstyleReference as replaceProjectArtstyleReferenceClient,
  updateProject as updateProjectClient,
  updateProjectArtDirection as updateProjectArtDirectionClient,
} from "@/features/projects/api/projectClient";
import type {
  ProjectArtDirectionUpdateInput,
  ProjectImageSettingsView,
} from "@/features/image-generation";
import {
  getCapacityState,
  removeProjectCard,
  selectProjectsByStatus,
  upsertProjectCard,
} from "@/features/projects/mappers/projectMappers";
import type {
  CreateProjectInput,
  ProjectAccessSelectionState,
  ProjectCapacitySummary,
  ProjectCardView,
  ProjectDashboardSummary,
  UpdateProjectInput,
  UpgradePromptState,
} from "@/types/projects";

export interface ProjectShellDependencies {
  addProjectArtstyleReference: typeof addProjectArtstyleReferenceClient;
  archiveProject: typeof archiveProjectClient;
  createProject: typeof createProjectClient;
  deleteProject: typeof deleteProjectClient;
  getCurrentProjectCapacity: typeof getCurrentProjectCapacity;
  getProjectAccessSelectionState: typeof getProjectAccessSelectionState;
  getProjectDashboardSummary: typeof getProjectDashboardSummary;
  getProjectImageSettings: typeof getProjectImageSettingsClient;
  listProjects: typeof listProjects;
  removeProjectArtstyleReference: typeof removeProjectArtstyleReferenceClient;
  reorderProjectArtstyleReferences: typeof reorderProjectArtstyleReferencesClient;
  replaceProjectArtstyleReference: typeof replaceProjectArtstyleReferenceClient;
  submitProjectAccessSelection: typeof submitProjectAccessSelectionClient;
  updateProject: typeof updateProjectClient;
  updateProjectArtDirection: typeof updateProjectArtDirectionClient;
}

export interface ProjectShellNotice {
  kind: "error" | "success";
  message: string;
}

export interface DeleteDialogTarget {
  projectId: string;
  projectTitle: string;
  warningText: string;
  confirmEnabled: boolean;
}

export interface ProjectShellStoreState {
  activeProjects: ProjectCardView[];
  addProjectArtstyleReference: (
    projectId: string,
    file: File,
  ) => Promise<ProjectImageSettingsView>;
  archivedProjects: ProjectCardView[];
  capacity: ProjectCapacitySummary | null;
  createDialogBusy: boolean;
  clearProjectImageSettings: () => void;
  currentProjectId: string | null;
  currentProjectSummary: ProjectDashboardSummary | null;
  dashboardStatus: "idle" | "loading" | "ready" | "missing" | "error";
  deleteDialog: {
    open: boolean;
    submitting: boolean;
    target: DeleteDialogTarget | null;
  };
  hydrateProjectImageSettings: (projectId: string) => Promise<ProjectImageSettingsView | null>;
  imageSettingsStatus: "idle" | "loading" | "ready" | "error";
  notice: ProjectShellNotice | null;
  portfolioStatus: "idle" | "loading" | "ready" | "error";
  projectAccessSelection: ProjectAccessSelectionState | null;
  projectAccessSelectionStatus: "idle" | "loading" | "ready" | "error";
  projectAccessSelectionSubmitting: boolean;
  projectImageSettings: ProjectImageSettingsView | null;
  projects: ProjectCardView[];
  removeProjectArtstyleReference: (
    projectId: string,
    referencePath: string,
  ) => Promise<ProjectImageSettingsView>;
  reorderProjectArtstyleReferences: (
    projectId: string,
    orderedPaths: string[],
  ) => Promise<ProjectImageSettingsView>;
  replaceProjectArtstyleReference: (
    projectId: string,
    referencePath: string,
    file: File,
  ) => Promise<ProjectImageSettingsView>;
  settingsBusy: boolean;
  upgradePrompt: UpgradePromptState | null;
  archiveProject: (projectId: string) => Promise<ProjectCardView>;
  clearNotice: () => void;
  clearUpgradePrompt: () => void;
  closeDeleteDialog: () => void;
  createProject: (input: CreateProjectInput) => Promise<ProjectCardView>;
  deleteProject: () => Promise<{ deletedProjectId: string }>;
  hydratePortfolio: () => Promise<void>;
  hydrateProjectDashboard: (projectId: string) => Promise<ProjectDashboardSummary | null>;
  submitProjectAccessSelection: (selectedProjectIds: string[]) => Promise<void>;
  openDeleteDialog: (project: ProjectCardView) => void;
  setNotice: (notice: ProjectShellNotice | null) => void;
  updateProjectArtDirection: (
    projectId: string,
    input: ProjectArtDirectionUpdateInput,
  ) => Promise<ProjectImageSettingsView>;
  updateProject: (
    projectId: string,
    input: UpdateProjectInput,
  ) => Promise<ProjectCardView>;
}

const defaultDependencies: ProjectShellDependencies = {
  addProjectArtstyleReference: addProjectArtstyleReferenceClient,
  archiveProject: archiveProjectClient,
  createProject: createProjectClient,
  deleteProject: deleteProjectClient,
  getCurrentProjectCapacity,
  getProjectAccessSelectionState,
  getProjectDashboardSummary,
  getProjectImageSettings: getProjectImageSettingsClient,
  listProjects,
  removeProjectArtstyleReference: removeProjectArtstyleReferenceClient,
  reorderProjectArtstyleReferences: reorderProjectArtstyleReferencesClient,
  submitProjectAccessSelection: submitProjectAccessSelectionClient,
  replaceProjectArtstyleReference: replaceProjectArtstyleReferenceClient,
  updateProject: updateProjectClient,
  updateProjectArtDirection: updateProjectArtDirectionClient,
};

function patchProjectCollections(projects: ProjectCardView[]) {
  return {
    activeProjects: selectProjectsByStatus(projects, "active"),
    archivedProjects: selectProjectsByStatus(projects, "archived"),
    projects,
  };
}

function getUpgradePrompt(error: unknown) {
  return error instanceof ProjectActionError ? error.upgradePrompt ?? null : null;
}

export function createProjectShellStore(
  deps: Partial<ProjectShellDependencies> = {},
) {
  const resolvedDeps: ProjectShellDependencies = {
    ...defaultDependencies,
    ...deps,
  };

  return createStore<ProjectShellStoreState>((set, get) => ({
    activeProjects: [],
    archivedProjects: [],
    capacity: null,
    createDialogBusy: false,
    currentProjectId: null,
    currentProjectSummary: null,
    dashboardStatus: "idle",
    deleteDialog: {
      open: false,
      submitting: false,
      target: null,
    },
    imageSettingsStatus: "idle",
    notice: null,
    portfolioStatus: "idle",
    projectAccessSelection: null,
    projectAccessSelectionStatus: "idle",
    projectAccessSelectionSubmitting: false,
    projectImageSettings: null,
    projects: [],
    settingsBusy: false,
    upgradePrompt: null,

    hydratePortfolio: async () => {
      set({
        notice: null,
        portfolioStatus: "loading",
        projectAccessSelectionStatus: "loading",
      });

      try {
        const [projects, capacity, projectAccessSelection] = await Promise.all([
          resolvedDeps.listProjects(),
          resolvedDeps.getCurrentProjectCapacity(),
          resolvedDeps.getProjectAccessSelectionState(),
        ]);

        set({
          ...patchProjectCollections(projects),
          capacity,
          portfolioStatus: "ready",
          projectAccessSelection,
          projectAccessSelectionStatus: "ready",
        });
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
          portfolioStatus: "error",
          projectAccessSelectionStatus: "error",
        });
      }
    },

    submitProjectAccessSelection: async (selectedProjectIds) => {
      set({
        notice: null,
        projectAccessSelectionSubmitting: true,
      });

      try {
        await resolvedDeps.submitProjectAccessSelection(selectedProjectIds);
        await get().hydratePortfolio();
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
        });
        throw error;
      } finally {
        set({
          projectAccessSelectionSubmitting: false,
        });
      }
    },

    hydrateProjectDashboard: async (projectId) => {
      set({
        currentProjectId: projectId,
        dashboardStatus: "loading",
      });

      try {
        const summary = await resolvedDeps.getProjectDashboardSummary(projectId);

        if (!summary) {
          set({
            currentProjectSummary: null,
            dashboardStatus: "missing",
          });
          return null;
        }

        const projects = upsertProjectCard(get().projects, summary);
        set({
          ...patchProjectCollections(projects),
          capacity: summary.capacity,
          currentProjectSummary: summary,
          dashboardStatus: "ready",
        });

        return summary;
      } catch (error) {
        set({
          currentProjectSummary: null,
          dashboardStatus: "error",
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
        });
        return null;
      }
    },

    hydrateProjectImageSettings: async (projectId) => {
      set({
        imageSettingsStatus: "loading",
        projectImageSettings: null,
      });

      try {
        const projectImageSettings = await resolvedDeps.getProjectImageSettings(projectId);

        set({
          imageSettingsStatus: "ready",
          projectImageSettings,
        });

        return projectImageSettings;
      } catch (error) {
        set({
          imageSettingsStatus: "error",
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
          projectImageSettings: null,
        });
        return null;
      }
    },

    createProject: async (input) => {
      set({
        createDialogBusy: true,
      });

      try {
        const project = await resolvedDeps.createProject(input);
        const capacity = await resolvedDeps.getCurrentProjectCapacity();

        const projects = upsertProjectCard(get().projects, project);

        set({
          ...patchProjectCollections(projects),
          capacity,
          createDialogBusy: false,
          notice: null,
          upgradePrompt: null,
        });

        return project;
      } catch (error) {
        set({
          createDialogBusy: false,
          upgradePrompt: getUpgradePrompt(error),
        });
        throw error;
      }
    },

    updateProject: async (projectId, input) => {
      set({ settingsBusy: true });

      try {
        const project = await resolvedDeps.updateProject(projectId, input);
        const capacity = await resolvedDeps.getCurrentProjectCapacity();

        const projects = upsertProjectCard(get().projects, project);
        const currentSummary = get().currentProjectSummary;

        set({
          ...patchProjectCollections(projects),
          capacity,
          currentProjectSummary:
            currentSummary?.projectId === project.projectId
              ? {
                  ...currentSummary,
                  ...project,
                  capacity,
                }
              : currentSummary,
          notice: {
            kind: "success",
            message: "projects.messages.saveSuccess",
          },
          settingsBusy: false,
        });

        return project;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
          settingsBusy: false,
        });
        throw error;
      }
    },

    updateProjectArtDirection: async (projectId, input) => {
      set({ settingsBusy: true });

      try {
        const projectImageSettings = await resolvedDeps.updateProjectArtDirection(
          projectId,
          input,
        );

        set({
          imageSettingsStatus: "ready",
          notice: {
            kind: "success",
            message: "projects.messages.saveSuccess",
          },
          projectImageSettings,
          settingsBusy: false,
        });

        return projectImageSettings;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
          settingsBusy: false,
        });
        throw error;
      }
    },

    addProjectArtstyleReference: async (projectId, file) => {
      set({ settingsBusy: true });

      try {
        const projectImageSettings = await resolvedDeps.addProjectArtstyleReference(
          projectId,
          file,
        );

        set({
          imageSettingsStatus: "ready",
          projectImageSettings,
          settingsBusy: false,
        });

        return projectImageSettings;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
          settingsBusy: false,
        });
        throw error;
      }
    },

    removeProjectArtstyleReference: async (projectId, referencePath) => {
      set({ settingsBusy: true });

      try {
        const projectImageSettings = await resolvedDeps.removeProjectArtstyleReference(
          projectId,
          referencePath,
        );

        set({
          imageSettingsStatus: "ready",
          projectImageSettings,
          settingsBusy: false,
        });

        return projectImageSettings;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
          settingsBusy: false,
        });
        throw error;
      }
    },

    replaceProjectArtstyleReference: async (projectId, referencePath, file) => {
      set({ settingsBusy: true });

      try {
        const projectImageSettings = await resolvedDeps.replaceProjectArtstyleReference(
          projectId,
          referencePath,
          file,
        );

        set({
          imageSettingsStatus: "ready",
          projectImageSettings,
          settingsBusy: false,
        });

        return projectImageSettings;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
          settingsBusy: false,
        });
        throw error;
      }
    },

    reorderProjectArtstyleReferences: async (projectId, orderedPaths) => {
      set({ settingsBusy: true });

      try {
        const projectImageSettings = await resolvedDeps.reorderProjectArtstyleReferences(
          projectId,
          orderedPaths,
        );

        set({
          imageSettingsStatus: "ready",
          projectImageSettings,
          settingsBusy: false,
        });

        return projectImageSettings;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
          settingsBusy: false,
        });
        throw error;
      }
    },

    archiveProject: async (projectId) => {
      try {
        const project = await resolvedDeps.archiveProject(projectId);
        const projects = upsertProjectCard(get().projects, project);
        const currentSummary = get().currentProjectSummary;

        set({
          ...patchProjectCollections(projects),
          currentProjectSummary:
            currentSummary?.projectId === project.projectId
              ? {
                  ...currentSummary,
                  ...project,
                }
              : currentSummary,
          notice: {
            kind: "success",
            message: "projects.messages.archiveSuccess",
          },
        });

        return project;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
        });
        throw error;
      }
    },

    openDeleteDialog: (project) => {
      set({
        deleteDialog: {
          open: true,
          submitting: false,
          target: {
            projectId: project.projectId,
            projectTitle: project.title,
            warningText: "projects.dialogs.deleteWarning",
            confirmEnabled: true,
          },
        },
      });
    },

    closeDeleteDialog: () => {
      set({
        deleteDialog: {
          open: false,
          submitting: false,
          target: null,
        },
      });
    },

    deleteProject: async () => {
      const target = get().deleteDialog.target;

      if (!target) {
        throw new ProjectActionError("not-found", "That project is unavailable.");
      }

      set({
        deleteDialog: {
          ...get().deleteDialog,
          submitting: true,
        },
      });

      try {
        const result = await resolvedDeps.deleteProject(target.projectId);
        const capacity = await resolvedDeps.getCurrentProjectCapacity();
        const projects = removeProjectCard(get().projects, result.deletedProjectId);
        const currentSummary = get().currentProjectSummary;

        set({
          ...patchProjectCollections(projects),
          capacity,
          currentProjectSummary:
            currentSummary?.projectId === result.deletedProjectId
              ? null
              : currentSummary,
          deleteDialog: {
            open: false,
            submitting: false,
            target: null,
          },
          notice: {
            kind: "success",
            message: "projects.messages.deleteSuccess",
          },
        });

        return result;
      } catch (error) {
        set({
          deleteDialog: {
            ...get().deleteDialog,
            submitting: false,
          },
          notice: {
            kind: "error",
            message: formatProjectErrorMessage(error),
          },
        });
        throw error;
      }
    },

    clearNotice: () => {
      set({ notice: null });
    },

    clearUpgradePrompt: () => {
      set({ upgradePrompt: null });
    },

    clearProjectImageSettings: () => {
      set({
        imageSettingsStatus: "idle",
        projectImageSettings: null,
      });
    },

    setNotice: (notice) => {
      set({ notice });
    },
  }));
}

export const projectShellStore = createProjectShellStore();

export function useProjectShellStore<T>(
  selector: (state: ProjectShellStoreState) => T,
) {
  return useStore(projectShellStore, selector);
}

export function selectCapacityState(state: Pick<ProjectShellStoreState, "capacity" | "upgradePrompt">) {
  return getCapacityState(state.capacity, state.upgradePrompt);
}
